import Splide from '@splidejs/splide'

const ProductSlider = {
  props: ['media', 'campaigns'],

  data() {
    return {
      showSlider: true,
      localMedia: [],
      _splideMain: null,
      _splideThumbs: null,
    }
  },

  computed: {
    normalizedMedia() {
      return this.localMedia || []
    },
  },

  methods: {
    // Attach product-level campaigns to images that lack them (non-destructive copy)
    normalizeMediaArray(srcArray) {
      const arr = Array.isArray(srcArray) ? srcArray : []
      return arr.map(img => {
        const base =
          img && typeof img === 'object' ? Object.assign({}, img) : {}

        if (
          (!base.Campaigns ||
            !Array.isArray(base.Campaigns) ||
            base.Campaigns.length === 0) &&
          Array.isArray(this.campaigns) &&
          this.campaigns.length
        ) {
          base.Campaigns = this.campaigns
        }

        return base
      })
    },

    // Return array of image URLs (strings) for campaigns attached to a given image.
    // Tolerant to small variations: Images/images and Url/url (ListUrl intentionally ignored).
    // Return array of image URLs (strings) for campaigns attached to a given image.
    // For main slides we use Url/url. For thumbnails we prefer ThumbnailUrl/thumbnailUrl, then ListUrl/listUrl, then derive from Url.
    getCampaignImageUrls(image, variant = 'main') {
      if (!image) return []

      const campaigns = image.Campaigns || image.campaigns || []
      if (!Array.isArray(campaigns) || campaigns.length === 0) return []

      const urls = campaigns
        .flatMap(c => {
          if (!c) return []
          const imgs = c.Images || c.images || []
          if (!Array.isArray(imgs) || imgs.length === 0) return []
          const first = imgs[0] || {}

          if (variant === 'thumb') {
            const thumb =
              first.ThumbnailUrl ||
              first.thumbnailUrl ||
              first.ListUrl ||
              first.listUrl

            if (thumb) return [thumb]
            const mainUrl = first.Url || first.url || ''
            const derived = this._deriveThumbnailFromMain(mainUrl)
            return [derived || mainUrl].filter(Boolean)
          }

          // default to main
          return [first.Url || first.url].filter(Boolean)
        })
        .filter(Boolean)

      return urls
    },

    // Combine explicit SealImages with campaign images for display badges
    getSealImages(image, variant = 'main') {
      if (!image) return []
      const seals = Array.isArray(image.SealImages) ? image.SealImages : []
      const campaignUrls = this.getCampaignImageUrls(image, variant)
      const combined = [...seals, ...campaignUrls].filter(Boolean)
      return Array.from(new Set(combined))
    },

    // Resolve main image URL for product media items
    getMainImageUrl(image) {
      if (!image) return ''
      return image.Url || image.url || image.Src || image.src || ''
    },

    // Resolve thumbnail image URL for product media items
    getThumbImageUrl(image) {
      if (!image) return ''
      const explicit =
        image.ThumbnailUrl ||
        image.thumbnailUrl ||
        image.ThumbSrc ||
        image.thumbSrc

      if (explicit) return explicit

      // Accept ListUrl as a thumbnail-only fallback if present
      const list = image.ListUrl || image.listUrl
      if (list) return list

      const main = this.getMainImageUrl(image)
      const derived = this._deriveThumbnailFromMain(main)
      return derived || main
    },

    // Attempt to derive a thumbnail URL from a main URL using common naming (_r -> _t)
    _deriveThumbnailFromMain(url) {
      if (!url || typeof url !== 'string') return ''
      // Replace trailing _r before extension with _t (case-insensitive)
      // Examples: image_r.jpg -> image_t.jpg
      const replaced = url.replace(/_r(\.[a-z0-9]+)$/i, '_t$1')
      if (replaced !== url) return replaced
      return ''
    },

    destroySliderInstances() {
      try {
        if (
          this._splideMain &&
          typeof this._splideMain.destroy === 'function'
        ) {
          this._splideMain.destroy(true)
          this._splideMain = null
        }
        if (
          this._splideThumbs &&
          typeof this._splideThumbs.destroy === 'function'
        ) {
          this._splideThumbs.destroy(true)
          this._splideThumbs = null
        }
      } catch (e) {
        console.warn('Error destroying Splide instances', e)
      }
    },

    initSlider() {
      this.destroySliderInstances()

      let root = null
      if (this.$el && this.$el.nodeType === 1) root = this.$el
      else if (this.$el && this.$el.$el && this.$el.$el.nodeType === 1)
        root = this.$el.$el
      else root = document.querySelector('product-slider') || document

      const mainEl =
        root && typeof root.querySelector === 'function'
          ? root.querySelector('[data-product-main]')
          : null
      const thumbsEl =
        root && typeof root.querySelector === 'function'
          ? root.querySelector('[data-product-thumbnails]')
          : null

      if (!mainEl || !thumbsEl) {
        console.debug(
          'ProductSlider: main or thumbs element not found, skipping init',
          {mainEl, thumbsEl, root}
        )
        return
      }

      this._splideThumbs = new Splide(thumbsEl, {
        arrows: this.normalizedMedia.length >= 5,
        focus: this.normalizedMedia.length >= 5 ? 'center' : 0,
        fixedHeight: 88,
        fixedWidth: 88,
        gap: 16,
        isNavigation: true,
        pagination: false,
        rewind: false,
      })

      this._splideMain = new Splide(mainEl, {
        arrows: false,
        drag: true,
        heightRatio: 1,
        lazyLoad: 'nearby',
        pagination: true,
      })

      this._splideThumbs.mount()
      this._splideMain.sync(this._splideThumbs).mount()

      // Small fallback: if badges still have data-src after mount, copy them to src so they're visible.
      // Remove this block when your lazy loader handles dynamically-added images.
      try {
        setTimeout(() => {
          const rootEl = root && root.querySelectorAll ? root : document
          const imgs = rootEl.querySelectorAll('.product-card__image')
          imgs.forEach(img => {
            if (!img) return
            const ds = img.getAttribute && img.getAttribute('data-src')
            if (ds && (!img.src || img.src.indexOf('data:image') === 0)) {
              img.src = ds
            }
          })
        }, 80)
      } catch (e) {
        // ignore
      }
    },
  },

  mounted() {
    this.localMedia = this.normalizeMediaArray(this.media || [])

    if (window && window.console)
      console.debug(
        'ProductSlider normalized sample:',
        this.localMedia[0] || null
      )

    this.showSlider = !!(this.localMedia && this.localMedia.length > 1)
    if (!this.showSlider) return

    this.$nextTick(() => {
      this.initSlider()
    })
  },

  watch: {
    media(newVal) {
      this.localMedia = this.normalizeMediaArray(newVal || [])
      this.showSlider = !!(this.localMedia && this.localMedia.length > 1)
      this.$nextTick(() => {
        this.initSlider()
      })
    },

    campaigns(newVal) {
      // If product-level campaigns change, re-normalize and refresh slider
      this.localMedia = this.normalizeMediaArray(this.media || [])
      this.$nextTick(() => {
        this.initSlider()
      })
    },
  },

  beforeDestroy() {
    this.destroySliderInstances()
  },
}

export default ProductSlider
